import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { MainLayout } from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useDataCache } from '@/contexts/DataCache';
import { PlugsConnected, Plus, Trash, Pencil, QrCode, Storefront, ArrowRight } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MLApiConfig() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const { products } = useDataCache();
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();

  const [status, setStatus] = useState({ connected: false });
  const [mappings, setMappings] = useState([]);
  const [dispatches, setDispatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState({ open: false, editing: null, draft: emptyDraft() });

  function emptyDraft() {
    return { ml_item_id: '', ml_title: '', product_id: '', variant_id: '', multiplier: 1, notes: '' };
  }

  const fetchAll = async () => {
    try {
      const [s, m, d] = await Promise.all([
        axios.get(`${API}/ml/status`, { withCredentials: true }),
        axios.get(`${API}/ml/mappings`, { withCredentials: true }),
        axios.get(`${API}/ml/dispatches?limit=20`, { withCredentials: true }),
      ]);
      setStatus(s.data || {});
      setMappings(m.data || []);
      setDispatches(d.data || []);
    } catch (e) {
      // silencioso
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  useEffect(() => {
    if (sp.get('ml_connected')) {
      toast.success('✅ Cuenta de Mercado Libre conectada');
      sp.delete('ml_connected'); setSp(sp, { replace: true });
    }
    const err = sp.get('ml_error');
    if (err) {
      toast.error(`Error ML: ${err}${sp.get('detail') ? ' — ' + sp.get('detail') : ''}`);
      sp.delete('ml_error'); sp.delete('detail'); setSp(sp, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sp]);

  const connect = async () => {
    try {
      const { data } = await axios.get(`${API}/ml/oauth/authorize`, { withCredentials: true });
      window.location.href = data.authorize_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al iniciar OAuth');
    }
  };

  const disconnect = async () => {
    if (!window.confirm('¿Desconectar la cuenta de Mercado Libre?')) return;
    try {
      await axios.post(`${API}/ml/disconnect`, {}, { withCredentials: true });
      toast.success('Desconectado');
      fetchAll();
    } catch (e) { toast.error('Error'); }
  };

  const openCreate = () => setDlg({ open: true, editing: null, draft: emptyDraft() });
  const openEdit = (m) => setDlg({
    open: true, editing: m,
    draft: {
      ml_item_id: m.ml_item_id || '',
      ml_title: m.ml_title || '',
      product_id: m.product_id || '',
      variant_id: m.variant_id || '',
      multiplier: m.multiplier || 1,
      notes: m.notes || '',
    },
  });

  const submitMapping = async () => {
    const d = dlg.draft;
    if (!d.ml_item_id.trim()) { toast.error('ml_item_id es obligatorio (ej: MLM123456789)'); return; }
    if (!d.product_id) { toast.error('Selecciona un producto POS'); return; }
    const payload = {
      ml_item_id: d.ml_item_id.trim().toUpperCase(),
      ml_title: d.ml_title.trim() || null,
      product_id: d.product_id,
      variant_id: d.variant_id.trim() || null,
      multiplier: Math.max(1, parseInt(d.multiplier) || 1),
      notes: d.notes.trim() || null,
    };
    try {
      if (dlg.editing) {
        await axios.put(`${API}/ml/mappings/${dlg.editing.id}`, payload, { withCredentials: true });
        toast.success('Mapeo actualizado');
      } else {
        await axios.post(`${API}/ml/mappings`, payload, { withCredentials: true });
        toast.success('Mapeo creado');
      }
      setDlg({ open: false, editing: null, draft: emptyDraft() });
      fetchAll();
    } catch (e) {
      // FastAPI 422 devuelve detail como array; normalizar a string
      const raw = e.response?.data?.detail;
      let msg;
      if (Array.isArray(raw)) {
        msg = raw.map(x => `${(x.loc || []).slice(-1)}: ${x.msg}`).join(' · ');
      } else if (typeof raw === 'string') {
        msg = raw;
      } else if (raw && typeof raw === 'object') {
        msg = JSON.stringify(raw);
      } else {
        msg = e.message || `HTTP ${e.response?.status || '?'} sin detalle`;
      }
      toast.error(`Error: ${msg}`);
    }
  };

  const deleteMapping = async (m) => {
    if (!window.confirm(`¿Eliminar mapeo ${m.ml_item_id}?`)) return;
    try {
      await axios.delete(`${API}/ml/mappings/${m.id}`, { withCredentials: true });
      toast.success('Eliminado');
      fetchAll();
    } catch (e) { toast.error('Error'); }
  };

  const sortedProducts = useMemo(
    () => [...(products || [])]
      .filter(p => p.visible_in_pos !== false)
      .sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [products]
  );

  return (
    <MainLayout>
      <div className="p-6 max-w-6xl mx-auto space-y-6">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <Storefront size={32} weight="bold" className="text-yellow-500" />
            <div>
              <h1 className="text-3xl font-bold tracking-tight">Mercado Libre — API</h1>
              <p className="text-sm text-muted-foreground">Conexión OAuth, mapeo de publicaciones y escáner de etiquetas</p>
            </div>
          </div>
          <Button onClick={() => navigate('/ml-scanner')} className="bg-blue-600 hover:bg-blue-700" data-testid="go-to-scanner-btn">
            <QrCode size={18} className="mr-1" /> Ir al scanner
            <ArrowRight size={14} className="ml-1" />
          </Button>
        </div>

        {/* Conexión OAuth */}
        <div className={`rounded-lg border p-5 ${status.connected ? 'bg-emerald-50 border-emerald-300' : 'bg-slate-50 border-slate-300'}`}>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <PlugsConnected size={28} weight={status.connected ? 'fill' : 'regular'} className={status.connected ? 'text-emerald-600' : 'text-slate-400'} />
              <div>
                <p className="font-semibold text-lg">{status.connected ? 'Conectado a Mercado Libre' : 'No conectado'}</p>
                {status.connected && (
                  <p className="text-xs text-slate-600">
                    User ID: <b>{status.user_id}</b>
                    {status.expires_at && <> · Token válido hasta {new Date(status.expires_at).toLocaleString('es-MX')}</>}
                  </p>
                )}
                {!status.connected && isAdmin && (
                  <p className="text-xs text-slate-500 mt-1">Solo admin. Requiere que estés en <b>producción</b> (redirect URI: <code>negocioexpress.com.mx</code>).</p>
                )}
              </div>
            </div>
            {isAdmin && (
              status.connected
                ? <Button variant="outline" onClick={disconnect} data-testid="ml-disconnect-btn">Desconectar</Button>
                : <Button onClick={connect} className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold" data-testid="ml-connect-btn">
                    Conectar Mercado Libre
                  </Button>
            )}
          </div>
        </div>

        {/* Mapeo ML ↔ POS */}
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <div className="p-4 border-b flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h2 className="font-semibold text-lg">Mapeo de publicaciones ML → productos POS</h2>
              <p className="text-xs text-slate-500 mt-0.5">Cada publicación ML (ej: pack de 5 Silla Victoria) se mapea a un producto POS con multiplicador.</p>
            </div>
            {isAdmin && (
              <Button onClick={openCreate} data-testid="add-mapping-btn"><Plus size={16} className="mr-1" /> Nuevo mapeo</Button>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="ml-mappings-table">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-slate-600 text-xs uppercase">ML Item ID</th>
                  <th className="px-3 py-2 text-left font-semibold text-slate-600 text-xs uppercase">Título ML</th>
                  <th className="px-3 py-2 text-left font-semibold text-slate-600 text-xs uppercase">Producto POS</th>
                  <th className="px-3 py-2 text-center font-semibold text-slate-600 text-xs uppercase">Piezas / envío</th>
                  <th className="px-3 py-2 text-left font-semibold text-slate-600 text-xs uppercase">Notas</th>
                  {isAdmin && <th className="px-3 py-2 text-right font-semibold text-slate-600 text-xs uppercase">Acciones</th>}
                </tr>
              </thead>
              <tbody className="divide-y">
                {loading && (<tr><td colSpan={isAdmin ? 6 : 5} className="px-3 py-6 text-center text-slate-400">Cargando…</td></tr>)}
                {!loading && mappings.length === 0 && (
                  <tr><td colSpan={isAdmin ? 6 : 5} className="px-3 py-6 text-center text-slate-500">
                    Sin mapeos. Agrega uno por cada publicación de ML.
                  </td></tr>
                )}
                {mappings.map(m => (
                  <tr key={m.id} className="hover:bg-slate-50" data-testid={`mapping-${m.id}`}>
                    <td className="px-3 py-2 font-mono text-xs">{m.ml_item_id}</td>
                    <td className="px-3 py-2">{m.ml_title || <span className="text-slate-400">—</span>}</td>
                    <td className="px-3 py-2">
                      {m.product_name || <span className="text-red-500">⚠️ Producto eliminado</span>}
                      {m.product_sku && <span className="text-[10px] text-slate-500 ml-1">({m.product_sku})</span>}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className="inline-flex items-center px-2 py-0.5 rounded bg-blue-100 text-blue-800 font-bold">×{m.multiplier}</span>
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500">{m.notes || ''}</td>
                    {isAdmin && (
                      <td className="px-3 py-2 text-right">
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="ghost" onClick={() => openEdit(m)} data-testid={`edit-mapping-${m.id}`}><Pencil size={14} /></Button>
                          <Button size="sm" variant="ghost" onClick={() => deleteMapping(m)} data-testid={`delete-mapping-${m.id}`}><Trash size={14} /></Button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Historial de envíos procesados */}
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <div className="p-4 border-b">
            <h2 className="font-semibold text-lg">Últimos envíos procesados</h2>
            <p className="text-xs text-slate-500 mt-0.5">Envíos ML escaneados y descontados del inventario.</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-slate-600 text-xs uppercase">Shipment ID</th>
                  <th className="px-3 py-2 text-left font-semibold text-slate-600 text-xs uppercase">Fecha</th>
                  <th className="px-3 py-2 text-left font-semibold text-slate-600 text-xs uppercase">Por</th>
                  <th className="px-3 py-2 text-left font-semibold text-slate-600 text-xs uppercase">Piezas descontadas</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {dispatches.length === 0 && (
                  <tr><td colSpan={4} className="px-3 py-6 text-center text-slate-500">Aún no se ha procesado ningún envío.</td></tr>
                )}
                {dispatches.map(d => (
                  <tr key={d.id} className="hover:bg-slate-50">
                    <td className="px-3 py-2 font-mono text-xs">{d.shipment_id}</td>
                    <td className="px-3 py-2 text-xs">{new Date(d.confirmed_at).toLocaleString('es-MX')}</td>
                    <td className="px-3 py-2">{d.confirmed_by_name || '—'}</td>
                    <td className="px-3 py-2 text-xs">
                      {(d.deducted || []).map((x, i) => (
                        <div key={i}><b>{x.product_name}</b> · −{x.qty} (stock ahora: {x.new_stock})</div>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Dialog crear/editar mapeo */}
      <Dialog open={dlg.open} onOpenChange={(o) => !o && setDlg({ open: false, editing: null, draft: emptyDraft() })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{dlg.editing ? 'Editar mapeo' : 'Nuevo mapeo ML → POS'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">ML Item ID *</Label>
              <Input
                placeholder="MLM123456789"
                value={dlg.draft.ml_item_id}
                onChange={(e) => setDlg(s => ({ ...s, draft: { ...s.draft, ml_item_id: e.target.value } }))}
                disabled={!!dlg.editing}
                data-testid="mapping-ml-item-id"
              />
              <p className="text-[10px] text-slate-500 mt-0.5">Cópialo de la URL de la publicación en Mercado Libre (después de <code>MLM</code>).</p>
            </div>
            <div>
              <Label className="text-xs">Título en ML (opcional)</Label>
              <Input
                placeholder='Ej: "Silla Victoria pack 5 piezas"'
                value={dlg.draft.ml_title}
                onChange={(e) => setDlg(s => ({ ...s, draft: { ...s.draft, ml_title: e.target.value } }))}
                data-testid="mapping-ml-title"
              />
            </div>
            <div>
              <Label className="text-xs">Producto POS *</Label>
              <select
                className="w-full border rounded px-2 py-2 text-sm"
                value={dlg.draft.product_id}
                onChange={(e) => setDlg(s => ({ ...s, draft: { ...s.draft, product_id: e.target.value } }))}
                data-testid="mapping-product-id"
              >
                <option value="">— Selecciona —</option>
                {sortedProducts.map(p => (
                  <option key={p.id} value={p.id}>{p.name}{p.sku ? ` (${p.sku})` : ''}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs">Piezas POS por 1 unidad ML *</Label>
              <Input
                type="number" min="1" step="1"
                value={dlg.draft.multiplier}
                onChange={(e) => setDlg(s => ({ ...s, draft: { ...s.draft, multiplier: e.target.value } }))}
                data-testid="mapping-multiplier"
              />
              <p className="text-[10px] text-slate-500 mt-0.5">Ej: si la publicación ML es &quot;pack de 5&quot;, pon <b>5</b>.</p>
            </div>
            <div>
              <Label className="text-xs">Notas (opcional)</Label>
              <Input
                value={dlg.draft.notes}
                onChange={(e) => setDlg(s => ({ ...s, draft: { ...s.draft, notes: e.target.value } }))}
                data-testid="mapping-notes"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setDlg({ open: false, editing: null, draft: emptyDraft() })}>Cancelar</Button>
              <Button onClick={submitMapping} data-testid="mapping-submit">{dlg.editing ? 'Guardar' : 'Crear'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
}
